import { replenishCards, spendSupports } from '../game/cardBattle';
import { applyBattleResult, applyMove, createGame } from '../game/game';
import type { FactionId, GameContext, GameState } from '../game/types';
import type { HistoryEntry } from './protocol';

/**
 * One entry of a history, as the server takes it: the rules' own step, and in a Card game the
 * hands as well, the supports each clash spent and the cards each round deals. The server, a
 * client that reloads and a replay all step through here, so a Card game rebuilt from its history
 * holds the hands the server holds (core-net-balance:quality#5).
 */
export function stepHistory(
  ctx: GameContext,
  state: GameState,
  entry: HistoryEntry,
  cards: boolean,
): GameState {
  const next =
    entry.kind === 'move'
      ? applyMove(ctx, state, entry.move)
      : applyBattleResult(
          ctx,
          entry.card ? spendSupports(ctx, state, entry.card, entry.rng) : state,
          entry.result,
          entry.rng,
        );
  return cards ? replenishCards(ctx, next) : next;
}

/**
 * Rebuilds a game from what the server recorded. A game is fully determined by its seed, the
 * number of players and the history, so a client that reloads its page (or joins late) reaches
 * the same state as everyone else without the server ever sending a state object. `cards` for a
 * game whose captures are settled on the Card table: its hands are part of the state.
 */
export function replayHistory(
  ctx: GameContext,
  options: {
    seed: number;
    players: number;
    playing?: FactionId[];
    engagement?: 'immediate' | 'muster';
    cards?: boolean;
  },
  history: readonly HistoryEntry[],
): GameState {
  let state = createGame(ctx, {
    seed: options.seed,
    players: options.players,
    playing: options.playing,
    engagement: options.engagement,
  });
  for (const entry of history) state = stepHistory(ctx, state, entry, options.cards ?? false);
  return state;
}
